import React, { useEffect } from 'react';
import Banner from '../components/common/Banner';
import SectionTitle from '../components/common/SectionTitle';
import CallToAction from '../components/common/CallToAction';
import { Award, BookOpen, Users, Target, Heart, Activity } from 'lucide-react';

const AboutPage: React.FC = () => {
  useEffect(() => {
    // Scroll to top when page loads
    window.scrollTo(0, 0);
    
    // Update document title
    document.title = 'About Us - VetEdu Consultants';
  }, []);

  return (
    <>
      <Banner 
        title="About VetEdu Consultants"
        subtitle="Your trusted partner for BVSc admissions in India"
        backgroundImage="https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
      />
      
      <section className="section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6 text-primary-700">Our Story</h2>
              <p className="text-gray-700 mb-4">
                VetEdu Consultants was founded in 2013 with a mission to guide aspiring veterinarians 
                through the complex admission process of veterinary colleges in India. What started as 
                a small advisory service has grown into one of India's leading education consultancies 
                specializing in BVSc admissions.
              </p>
              <p className="text-gray-700 mb-4">
                Our founder, Dr. Rajesh Kumar, a veterinarian himself, recognized the challenges 
                students face when pursuing veterinary education. He assembled a team of experienced 
                educators, counselors, and veterinary professionals dedicated to helping students 
                navigate the admission process successfully.
              </p>
              <p className="text-gray-700">
                Over the years, we have helped thousands of students secure admissions in prestigious 
                veterinary colleges across India. Our personalized approach, attention to detail, and 
                commitment to student success have earned us a reputation for excellence in the field.
              </p>
            </div>
            
            <div className="rounded-lg overflow-hidden shadow-lg">
              <img 
                src="https://images.pexels.com/photos/5905445/pexels-photo-5905445.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" 
                alt="VetEdu Consultants Team" 
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>
      
      <section className="section bg-gray-50">
        <div className="container-custom">
          <SectionTitle 
            title="Our Mission & Vision" 
            subtitle="We are driven by our commitment to excellence in veterinary education guidance."
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
            <div className="bg-white p-8 rounded-lg shadow-md border-t-4 border-primary-500">
              <div className="flex items-center mb-4">
                <Target size={32} className="text-primary-500 mr-4" />
                <h3 className="text-2xl font-semibold">Our Mission</h3>
              </div>
              <p className="text-gray-700">
                To empower aspiring veterinarians with expert guidance, personalized support, and 
                comprehensive resources to navigate the admission process successfully. We aim to make 
                quality veterinary education accessible to deserving candidates by providing transparent, 
                ethical, and result-oriented counseling services.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-lg shadow-md border-t-4 border-secondary-500">
              <div className="flex items-center mb-4">
                <Heart size={32} className="text-secondary-500 mr-4" />
                <h3 className="text-2xl font-semibold">Our Vision</h3>
              </div>
              <p className="text-gray-700">
                To be India's most trusted and comprehensive resource for veterinary education guidance, 
                recognized for our integrity, expertise, and commitment to student success. We envision 
                a future where every aspiring veterinarian has access to quality education and guidance, 
                contributing to the advancement of animal healthcare in India.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      <section className="section">
        <div className="container-custom">
          <SectionTitle 
            title="Our Values" 
            subtitle="The principles that guide our approach to education consultancy."
          />
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 mt-8">
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
                <Award size={32} className="text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-primary-700">Excellence</h3>
              <p className="text-gray-600">
                We strive for excellence in all aspects of our service, maintaining the highest 
                standards of professionalism, knowledge, and guidance.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
                <Users size={32} className="text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-primary-700">Student-Centered</h3>
              <p className="text-gray-600">
                We put students first, tailoring our approach to meet individual needs, aspirations, 
                and circumstances with empathy and personalized attention.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
                <Activity size={32} className="text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-primary-700">Integrity</h3>
              <p className="text-gray-600">
                We operate with honesty, transparency, and ethical practices, providing accurate 
                information and realistic guidance to our students.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
                <BookOpen size={32} className="text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-primary-700">Knowledge</h3>
              <p className="text-gray-600">
                We continuously update our knowledge of veterinary education, admission processes, 
                and industry trends to provide the most relevant guidance.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
                <Heart size={32} className="text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-primary-700">Compassion</h3>
              <p className="text-gray-600">
                We understand the stress associated with admissions and approach our guidance with 
                empathy, patience, and genuine care for student well-being.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
                <Target size={32} className="text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-primary-700">Results-Driven</h3>
              <p className="text-gray-600">
                We are committed to achieving tangible results for our students, focusing on successful 
                admissions and long-term career satisfaction.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      <section className="section bg-gray-50">
        <div className="container-custom">
          <SectionTitle 
            title="Our Approach" 
            subtitle="How we work with students to ensure their success in veterinary admissions."
          />
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mt-8">
            <div className="order-2 lg:order-1">
              <h3 className="text-2xl font-semibold mb-4 text-primary-700">
                What We Do
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-500 flex items-center justify-center text-white font-bold mr-3 mt-1">
                    1
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-800">Personalized Assessment</h4>
                    <p className="text-gray-600">
                      We evaluate each student's academic profile, career goals, and preferences to provide 
                      tailored guidance.
                    </p>
                  </div>
                </li>
                
                <li className="flex items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-500 flex items-center justify-center text-white font-bold mr-3 mt-1">
                    2
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-800">College Selection</h4>
                    <p className="text-gray-600">
                      We help students identify the best veterinary colleges based on their NEET scores, 
                      budget, location preferences, and career aspirations.
                    </p>
                  </div>
                </li>
                
                <li className="flex items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-500 flex items-center justify-center text-white font-bold mr-3 mt-1">
                    3
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-800">Application Support</h4>
                    <p className="text-gray-600">
                      We provide comprehensive assistance with application forms, document preparation, 
                      and submission to ensure error-free applications.
                    </p>
                  </div>
                </li>
                
                <li className="flex items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-500 flex items-center justify-center text-white font-bold mr-3 mt-1">
                    4
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-800">Continuous Support</h4>
                    <p className="text-gray-600">
                      We provide ongoing guidance throughout the admission process, from counseling to 
                      final enrollment, and even post-admission support.
                    </p>
                  </div>
                </li>
              </ul>
            </div>
            
            <div className="order-1 lg:order-2">
              <img 
                src="https://images.pexels.com/photos/8942988/pexels-photo-8942988.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" 
                alt="Our Approach" 
                className="rounded-lg shadow-lg w-full h-auto"
              />
            </div>
          </div>
          
          <div className="mt-16">
            <h3 className="text-2xl font-semibold mb-6 text-center text-primary-700">
              How We Make a Difference
            </h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-5 rounded-lg shadow-md text-center">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users size={24} className="text-primary-600" />
                </div>
                <h4 className="font-semibold mb-2">Expert Team</h4>
                <p className="text-gray-600 text-sm">
                  Our counselors combine academic expertise with industry knowledge for comprehensive guidance.
                </p>
              </div>
              
              <div className="bg-white p-5 rounded-lg shadow-md text-center">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award size={24} className="text-primary-600" />
                </div>
                <h4 className="font-semibold mb-2">College Networks</h4>
                <p className="text-gray-600 text-sm">
                  We maintain strong relationships with veterinary colleges across India for better placement opportunities.
                </p>
              </div>
              
              <div className="bg-white p-5 rounded-lg shadow-md text-center">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <BookOpen size={24} className="text-primary-600" />
                </div>
                <h4 className="font-semibold mb-2">Updated Resources</h4>
                <p className="text-gray-600 text-sm">
                  We provide the latest information on admission procedures, curriculum, and career opportunities.
                </p>
              </div>
              
              <div className="bg-white p-5 rounded-lg shadow-md text-center">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart size={24} className="text-primary-600" />
                </div>
                <h4 className="font-semibold mb-2">Student Success</h4>
                <p className="text-gray-600 text-sm">
                  Our ultimate goal is your success, measured by admissions, academic achievements, and career satisfaction.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <CallToAction 
        title="Begin Your Veterinary Education Journey"
        description="Contact us today for expert guidance and take the first step toward your dream career."
        buttonText="Contact Us"
        buttonLink="/contact"
      />
    </>
  );
};

export default AboutPage;